import { Box, CircularProgress } from '@material-ui/core';
import { ExtractorDocumentType } from '@nemuru-eng/js-api-client';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FileEntryRow } from '@/components/shared/components/DocumentExtractorDialog/components/FileEntryRow/FileEntryRow';
import {
  ExtractionResult,
  useDocumentExtractorContext,
} from '@/components/shared/components/DocumentExtractorDialog/contexts/DocumentExtractorContext';
import {
  ACCEPTED_EXTENSIONS,
  MAX_FILE_SIZE_MB,
  useDocumentExtractorActions,
} from '@/components/shared/components/DocumentExtractorDialog/hooks/useDocumentExtractorActions';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { useGoogleMaps } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/hooks/useGoogleMaps';
import {
  Geocode,
  Prediction,
} from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/components/GoogleMapsAutocomplete/types';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { ENV_MAPS_API_KEY } from '@/config/envVars';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export interface GeocodingFunctions {
  retrievePredictions: (input: string) => Promise<Prediction[]>;
  geocodePlace: (placeId: string) => Promise<Geocode>;
}

export interface ConfirmedEntry {
  file: File;
  documentType?: ExtractorDocumentType;
}

export interface DocumentExtractorDialogProps {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  mapValues: (results: ExtractionResult[], geocoding: GeocodingFunctions) => Promise<Record<string, unknown>>;
  onConfirmed?: (entries: ConfirmedEntry[]) => void;
}

export const DocumentExtractorDialog = ({
  opened,
  setOpened,
  mapValues,
  onConfirmed,
}: DocumentExtractorDialogProps) => {
  const { palette } = useCustomTheme();
  const { getTextIdTranslation: t } = useTranslation();
  const { setValue } = useFormContext();
  const { load, retrievePredictions, geocodePlace } = useGoogleMaps();
  const { entries, fileErrors, apiError, isExtracting, dragState } = useDocumentExtractorContext();
  const { addFiles, handleExtract } = useDocumentExtractorActions();

  const inputRef = React.useRef<HTMLInputElement>(null);

  const [isFileDragging, setIsFileDragging] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showPrefillConfirm, setShowPrefillConfirm] = useState(false);

  useEffect(() => {
    if (opened) load(ENV_MAPS_API_KEY);
  }, [opened]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsFileDragging(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (dragState.source !== null) return;
    setIsFileDragging(true);
  };

  const handleFileDragLeave = () => setIsFileDragging(false);

  const handleClose = () => {
    if (isExtracting || isConfirming) return;
    setOpened(false);
  };

  const handleConfirm = async () => {
    setShowPrefillConfirm(false);
    setIsConfirming(true);
    try {
      const successfulResults = [...entries]
        .reverse()
        .filter((e) => e.status === 'succeeded' && e.result)
        .map((e) => e.result!);

      const values = await mapValues(successfulResults, { retrievePredictions, geocodePlace });
      const setFields = new Set<string>();

      for (const [key, value] of Object.entries(values)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setValue(key as any, value as any, { shouldDirty: true, shouldValidate: true });
        setFields.add(key);
      }

      // if (setFields.size > 0) {
      //   markAsPreFilled(setFields);
      // }

      // We include failed entries too, so we can upload them to the API
      const confirmedEntries: ConfirmedEntry[] = entries
        // .filter((e) => e.status === 'succeeded')
        .map((e) => ({ file: e.file, documentType: e.result?.documentType ?? e.assignedDocumentType }));

      if (confirmedEntries.length > 0) {
        onConfirmed?.(confirmedEntries);
      }
    } finally {
      setIsConfirming(false);
    }

    handleClose();
  };

  const hasPending = entries.some((e) => e.status === 'pending');
  const hasSucceeded = entries.some((e) => e.status === 'succeeded');
  const isBusy = isExtracting || isConfirming;

  return (
    <>
      <ActionDialog
        opened={showPrefillConfirm}
        setOpened={setShowPrefillConfirm}
        handleClose={() => setShowPrefillConfirm(false)}
        Title={<>{t('DOCUMENT_EXTRACTOR_PREFILL_CONFIRM_TITLE')}</>}
        Description={<>{t('DOCUMENT_EXTRACTOR_PREFILL_CONFIRM_DESCRIPTION')}</>}
        MainButton={<>{t('DOCUMENT_EXTRACTOR_CONFIRM_AND_PREFILL')}</>}
        onSubmit={handleConfirm}
        dialogProps={{ maxWidth: 'sm', disableBackdropClick: true }}
      />
      <NemuruDialog
        opened={opened}
        setOpened={setOpened}
        onClose={handleClose}
        showClose={!isBusy}
        dialogProps={{ maxWidth: 'md', disableBackdropClick: true }}
        paperProps={{ padding: '0 8px' }}
        removeDefaultButton
        buttons={
          <>
            <StandardButton disabled={isBusy} onClick={handleClose}>
              <Text tId="CLOSE" />
            </StandardButton>
            {hasPending && (
              <StandardButton main disabled={isBusy} onClick={handleExtract}>
                {t('DOCUMENT_EXTRACTOR_EXTRACT')}
              </StandardButton>
            )}
            {hasSucceeded && !hasPending && (
              <StandardButton main disabled={isBusy} onClick={() => setShowPrefillConfirm(true)}>
                {t('DOCUMENT_EXTRACTOR_CONFIRM_AND_PREFILL')}
              </StandardButton>
            )}
          </>
        }
      >
        <Box mb={4}>
          <Box mb={1}>
            <Font variant="h3" color="title">
              <Text tId="DOCUMENT_EXTRACTOR_DIALOG_TITLE" />
            </Font>
          </Box>
          <Font variant="body2" color="textSecondary">
            {t('DOCUMENT_EXTRACTOR_DIALOG_DESCRIPTION')}
          </Font>
        </Box>
        <Box display="flex" flexDirection="column" gridGap={24} minHeight={500}>
          {isBusy ? (
            <CustomPaper fill="grey.light" style={{ padding: 24, minHeight: 72 }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gridGap={12}
                height="100%"
                justifyContent="center"
              >
                <CircularProgress size={32} color="secondary" />
                <Font variant="body2" color="textSecondary">
                  {isConfirming ? t('DOCUMENT_EXTRACTOR_APPLYING') : t('DOCUMENT_EXTRACTOR_EXTRACTING')}
                </Font>
              </Box>
            </CustomPaper>
          ) : (
            <CustomPaper
              fill="grey.light"
              style={{
                minHeight: 72,
                padding: 24,
                textAlign: 'center' as const,
                cursor: 'pointer',
                boxShadow: isFileDragging ? `0 0 0 2px ${palette.primary.main}` : `0 0 0 1px ${palette.grey[300]}`,
                ...(isFileDragging && { backgroundColor: `${palette.primary.main}14` }),
              }}
              onClick={() => inputRef.current?.click()}
              onDrop={handleFileDrop}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
            >
              <Box
                color={palette.text.primary}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gridGap={4}
                height="100%"
              >
                <Icon style={{ paddingBottom: 4 }}>upload_file</Icon>
                <Font variant="body2">{t('DOCUMENT_EXTRACTOR_DROP_FILES')}</Font>
                <Font variant="caption" color="textSecondary">
                  {ACCEPTED_EXTENSIONS.join(', ')} — {t('DOCUMENT_EXTRACTOR_FILE_SIZE_LIMIT_PREFIX')} {MAX_FILE_SIZE_MB}{' '}
                  {t('DOCUMENT_EXTRACTOR_FILE_SIZE_LIMIT_SUFFIX')}
                </Font>
              </Box>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                multiple
                style={{ display: 'none' }}
                onChange={handleInputChange}
              />
            </CustomPaper>
          )}

          {entries.length > 0 && (
            <Box display="flex" flexDirection="column" gridGap={12}>
              {entries.length > 1 && (
                <Box display="flex" alignItems="center" gridGap={4} mb={1}>
                  <Icon size={14} color={palette.text.secondary}>
                    drag_indicator
                  </Icon>
                  <Font variant="caption" color="textSecondary">
                    {t('DOCUMENT_EXTRACTOR_DRAG_REORDER_HINT')}
                  </Font>
                </Box>
              )}
              {entries.map((entry, index) => (
                <FileEntryRow key={entry.file.name} entry={entry} index={index} />
              ))}
            </Box>
          )}

          {fileErrors.length > 0 && (
            <Box display="flex" flexDirection="column" gridGap={2}>
              {fileErrors.map((error, i) => (
                <Font key={i} variant="caption" style={{ color: palette.error.main }}>
                  {error}
                </Font>
              ))}
            </Box>
          )}

          {apiError && (
            <Font variant="body2" style={{ color: palette.error.main }}>
              {apiError}
            </Font>
          )}
        </Box>
      </NemuruDialog>
    </>
  );
};
